import { createIntl, FormattedMessage, IntlProvider } from 'react-intl';
import Stack from '@mui/material/Stack';
import Slider from '@mui/material/Slider';
import React from 'react';
import Fade from '@mui/material/Fade';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import ar from './ar.json';
import en from './en.json';
import { LocaleContext } from '../../../contexts/language';
import { BrandContext } from '../../../contexts/branding';
import Link from '@mui/material/Link';
import { useFormHandlers } from './useFormHandlers';

const ZeroDepositModalForm = React.lazy(() => import('../Forms/ZeroDepositModalForm'));
const languages = {
  ar,
  en
};
const FinanceCalculator = ({
  upfrontAmount, setUpfrontAmount,
  numberMonths, setNumberMonths,
  minUpfrontAmount, maxUpfrontAmount,
  minNumberMonths, maxNumberMonths,
  currency, purchaseAmount,

}) => {
  const { addEnquiry } = useFormHandlers();
  const localeConfig = React.useContext(LocaleContext);
  const brandConfig = React.useContext(BrandContext);

  const loanTerm = numberMonths ? numberMonths : maxNumberMonths;
  const [showForm, setShowForm] = React.useState(false);
  const intl = createIntl({
    locale: localeConfig?.lang || 'en',
    messages: languages[localeConfig?.lang] || languages.en
  });
  console.log({ upfrontAmount });
  return (
    <IntlProvider locale={localeConfig?.lang || 'en'} messages={languages[localeConfig?.lang] || languages.en}>
      <Fade in>
        <Stack spacing={2}>
          <Stack>
            <React.Suspense fallback={null}>
              <ZeroDepositModalForm
                onClose={() => setShowForm(false)}
                open={showForm}
                onSubmit={addEnquiry}
              />
            </React.Suspense>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600 }}>
              {
                intl.formatMessage({
                  id: 'financeCalculator.downPayment.labelSlider',
                  defaultMessage: 'How much would you like to pay as a down payment?'
                })
              }
            </Typography>
            <Typography
              sx={{
                fontSize: '12px', fontWeight: 400, color: '#5F738C'
              }}
            >
              {intl.formatMessage({
                id: 'financeCalculator.downPayment.description',
                defaultMessage: 'Pay {amount} Down Payment ({asPercentage}%)'
              }, {
                amount: intl.formatNumber(upfrontAmount, {
                  style: 'currency',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                  currency
                }),
                asPercentage: parseInt(upfrontAmount / purchaseAmount * 100)
              })}
            </Typography>
            <Slider
              aria-label={intl.formatMessage({
                id: 'financeCalculator.downPayment.ariaLabelSlider',
                defaultMessage: 'How much would you like to pay as a down payment?'
              })}
              aria-valuetext={intl.formatNumber(upfrontAmount, {
                style: 'currency',
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                currency
              })}
              defaultValue={upfrontAmount}
              max={maxUpfrontAmount}
              min={minUpfrontAmount}
              onChange={(v) => setUpfrontAmount(v.target.value)}
              step={(purchaseAmount) / 20}

            />
          </Stack>

          <Typography
            sx={{ fontSize: '12px' }}
          >
            <FormattedMessage id={'financeCalculator.depositDisclaimer'}
              defaultMessage={'Estimated profit rate used is {APR}%.'}
              values={{ APR: brandConfig.finance.financeRate }} />&nbsp;
            {brandConfig.finance.extraTerms ? brandConfig.finance.extraTerms : (
              <FormattedMessage
                values={{
                  link: chunks => (
                    <Link
                      underline={'hover'}
                      component={'button'}
                      onClick={() => {
                        setShowForm(true);
                      }}>
                      {chunks}
                    </Link>
                  )
                }}
                id={'financeCalculator.depositDisplaimerExtraTerms'}
                defaultMessage={'You may be able to buy this car with zero down payment, <link>contact us</link> to see if we can help.'}
              />
            )}
          </Typography>
          <Stack>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600 }}>
              {
                intl.formatMessage({
                  id: 'financeCalculator.loanTerm.labelSlider',
                  defaultMessage: 'Over How Many Years?'
                })
              }
            </Typography>
            <Typography
              sx={{
                fontSize: '12px', fontWeight: 400, color: '#5F738C'
              }}
            >
              {
                intl.formatMessage({
                  id: 'financeCalculator.loanTerm.valueDescription',
                  defaultMessage: 'Pay over {term} Years'
                }, {
                  term: intl.formatNumber(loanTerm / 12, {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                  })
                })
              }
            </Typography>
            <Slider
              aria-label={intl.formatMessage({
                id: 'financeCalculator.loanTerm.ariaLabelSlider',
                defaultMessage: 'Over How Many Years?'
              })}
              aria-valuetext={intl.formatNumber(loanTerm / 12, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
              defaultValue={loanTerm}
              max={maxNumberMonths}
              min={minNumberMonths}
              onChange={(e) => setNumberMonths(e.target.value)}
              step={12}
            />
          </Stack>
        </Stack>
      </Fade>
    </IntlProvider>
  );
};

FinanceCalculator.propTypes = {
  upfrontAmount: PropTypes.number,
  setUpfrontAmount: PropTypes.func.isRequired,
  numberMonths: PropTypes.number,
  setNumberMonths: PropTypes.func.isRequired,
  minUpfrontAmount: PropTypes.number,
  maxUpfrontAmount: PropTypes.number,
  minNumberMonths: PropTypes.number,
  maxNumberMonths: PropTypes.number,
  currency: PropTypes.string,
  purchaseAmount: PropTypes.number,
  trimLevel: PropTypes.shape({}),
  offer: PropTypes.shape({})
};

export default FinanceCalculator;
export { FinanceCalculator };